import { UseAnimationCssProperties } from '#hooks/UNSAFE_useAnimation/animationUtils';
import { useAnimation } from '#hooks/UNSAFE_useAnimation/useAnimation';

type AnimationStates = Parameters<typeof useAnimation>[1]['animationStates'];
type AnimationState = AnimationStates[string];
type AnimationStateFunction = Extract<AnimationStates[string], (node: HTMLElement) => object>;
type AnimationStateObject = ReturnType<AnimationStateFunction>;

// TODO: All these values should be obtained via theming tools instead of using constants: 
// Easing
type TimingFunction = Required<Required<AnimationStateObject>['options']>['easing'];
const EASING_ENTER: TimingFunction = [0, 0, 0.2, 1];
const EASING_PERSIST: TimingFunction = [0.4, 0, 0.2, 1];
const EASING_EXIT: TimingFunction = [0.4, 0, 1, 1];
const EASING_LiNEAR: TimingFunction = [0, 0, 0, 0];

// Duration
const DURATION_XXSMALL = 25 as number;
const DURATION_XSMALL = 50 as number;
const DURATION_SMALL = 100 as number;
const DURATION_MEDIUM = 150 as number;
const DURATION_LARGE = 250 as number;
const DURATION_XLARGE = 300 as number;
const DURATION_XXLARGE = 500 as number;

//DELAY
const DELAY_NONE = 0 as number;
const DELAY_SMALL = 20 as number;
const DELAY_MEDIUM = 30 as number;
const DELAY_LARGE = 50 as number;

//Anchor
type TransformOrigin = UseAnimationCssProperties['transformOrigin'];
const ANCHOR_DEFAULT: TransformOrigin = 'center';

const ANCHOR_TOP_LEFT: TransformOrigin = 'top left';

const ANCHOR_TOP_CENTER: TransformOrigin = 'top center';

const ANCHOR_TOP_RIGHT: TransformOrigin = 'top right';

const ANCHOR_LEFT_CENTER: TransformOrigin = 'left center';

const ANCHOR_RIGHT_CENTER: TransformOrigin = 'right center';

const ANCHOR_BOTTOM_LEFT: TransformOrigin = 'bottom left';

const ANCHOR_BOTTOM_CENTER: TransformOrigin = 'bottom center';

const ANCHOR_BOTTOM_RIGHT: TransformOrigin = 'bottom right';

// Fade
const FADE_IN_SMALL: AnimationStateObject = {
  from: {
    opacity: 0.6
  },
  to: {
    opacity: 1
  }
};

const FADE_IN_FULL: AnimationStateObject = {
  from: {
    opacity: 0
  },
  to: {
    opacity: 1
  }
};
const FADE_OUT_SMALL: AnimationStateObject = {
  from: {
    opacity: 1
  },
  to: {
    opacity: 0.6
  }
};

const FADE_OUT_FULL: AnimationStateObject = {
  from: {
    opacity: 1
  },
  to: {
    opacity: 0
  }
};

// Scale
const SCALE_DOWN_SMALL: AnimationStateObject = {
  from: {
    scaleX: 1,
    scaleY: 1
  },
  to: {
    scaleX: 0.9,
    scaleY: 0.9
  }
};

const SCALE_DOWN_MEDIUM: AnimationStateObject = {
  from: {
    scaleX: 1,
    scaleY: 1
  },
  to: {
    scaleX: 0.7,
    scaleY: 0.7
  }
};

const SCALE_DOWN_FULL: AnimationStateObject = {
  from: {
    scaleX: 1,
    scaleY: 1
  },
  to: {
    scaleX: 0,
    scaleY: 0
  }
};

const SCALE_UP_SMALL: AnimationStateObject = {
  from: {
    scaleX: 0.9,
    scaleY: 0.9
  },
  to: {
    scaleX: 1,
    scaleY: 1
  }
};

const SCALE_UP_MEDIUM: AnimationStateObject = {
  from: {
    scaleX: 0.7,
    scaleY: 0.7
  },
  to: {
    scaleX: 1,
    scaleY: 1
  }
};

const SCALE_UP_FULL: AnimationStateObject = {
  from: {
    scaleX: 0,
    scaleY: 0
  },
  to: {
    scaleX: 1,
    scaleY: 1
  }
};

// Slide
const SLIDE_UP_SMALL: AnimationStateObject = {
  from: {
    translateY: `25%`
  },
  to: {
    translateY: '0'
  }
};

const SLIDE_UP_MEDIUM: AnimationStateObject = {
  from: {
    translateY: `50%`
  },
  to: {
    translateY: '0'
  }
};

const SLIDE_UP_LARGE: AnimationStateObject = {
  from: {
    translateY: `75%`
  },
  to: {
    translateY: '0'
  }
};
const SLIDE_UP_XLARGE: AnimationStateObject = {
  from: {
    translateY: `100%`
  },
  to: {
    translateY: '0'
  }
};

const SLIDE_DOWN_SMALL: AnimationStateObject = {
  from: {
    translateY: '0'
  },
  to: {
    translateY: `25%`
  }
};

const SLIDE_DOWN_MEDIUM: AnimationStateObject = {
  from: {
    translateY: '0'
  },
  to: {
    translateY: `50%`
  }
};

const SLIDE_DOWN_LARGE: AnimationStateObject = {
  from: {
    translateY: '0'
  },
  to: {
    translateY: `75%`
  }
};
const SLIDE_DOWN_XLARGE: AnimationStateObject = {
  from: {
    translateY: '0'
  },
  to: {
    translateY: `100%`
  }
};

const SLIDE_START_SMALL: AnimationStateObject = {
  from: {
    translateX: `25%`
  },
  to: {
    translateX: '0'
  }
};

const SLIDE_START_MEDIUM: AnimationStateObject = {
  from: {
    translateX: `50%`
  },
  to: {
    translateX: '0'
  }
};

const SLIDE_START_LARGE: AnimationStateObject = {
  from: {
    translateX: `75%`
  },
  to: {
    translateX: '0'
  }
};

const SLIDE_START_XLARGE: AnimationStateObject = {
  from: {
    translateX: `100%`
  },
  to: {
    translateX: '0'
  }
};

const SLIDE_END_SMALL: AnimationStateObject = {
  from: {
    translateY: '0'
  },
  to: {
    translateY: `25%`
  }
};

const SLIDE_END_MEDIUM: AnimationStateObject = {
  from: {
    translateX: '0'
  },
  to: {
    translateX: `50%`
  }
};

const SLIDE_END_LARGE: AnimationStateObject = {
  from: {
    translateX: '0'
  },
  to: {
    translateX: `75%`
  }
};

const SLIDE_END_XLARGE: AnimationStateObject = {
  from: {
    translateX: '0'
  },
  to: {
    translateX: `100%`
  }
};

/**
 * Take multiple animation configurations and merge them to create a single one. If options param is passed
 * it overides values passes via that parameter.
 *
 * @param effects
 * @param options
 * @returns Animation configuration
 */
function mergeAnimationStateObjects(
  effects: Array<AnimationStateObject>,
  options?: AnimationStateObject['options']
): AnimationStateObject {
  let animationConfig: AnimationStateObject = {
    from: {},
    to: {},
    end: {},
    options: {}
  };
  effects.forEach((effect) => {
    animationConfig = {
      from: {
        ...animationConfig.from,
        ...effect.from
      },
      to: {
        ...animationConfig.to,
        ...effect.to
      },
      end: {
        ...animationConfig.end,
        ...effect.end
      },
      options: {
        ...animationConfig.options,
        ...effect.options
      }
    };
  });

  const { options: animationConfigOptions, ...fromToEnd } = animationConfig;
  return { ...fromToEnd, options: { ...animationConfigOptions, ...options } };
}

export {
  type AnimationStates,
  type AnimationState,
  type AnimationStateFunction,
  type AnimationStateObject,
  EASING_ENTER,
  EASING_EXIT,
  EASING_PERSIST,
  EASING_LiNEAR,
  DURATION_XXSMALL,
  DURATION_XSMALL,
  DURATION_SMALL,
  DURATION_MEDIUM,
  DURATION_LARGE,
  DURATION_XLARGE,
  DURATION_XXLARGE,
  DELAY_NONE,
  DELAY_SMALL,
  DELAY_MEDIUM,
  DELAY_LARGE,
  ANCHOR_DEFAULT,
  ANCHOR_TOP_LEFT,
  ANCHOR_TOP_CENTER,
  ANCHOR_TOP_RIGHT,
  ANCHOR_LEFT_CENTER,
  ANCHOR_RIGHT_CENTER,
  ANCHOR_BOTTOM_LEFT,
  ANCHOR_BOTTOM_CENTER,
  ANCHOR_BOTTOM_RIGHT,
  SLIDE_UP_SMALL,
  SLIDE_UP_MEDIUM,
  SLIDE_UP_LARGE,
  SLIDE_UP_XLARGE,
  SLIDE_DOWN_SMALL,
  SLIDE_DOWN_MEDIUM,
  SLIDE_DOWN_LARGE,
  SLIDE_DOWN_XLARGE,
  SLIDE_START_SMALL,
  SLIDE_START_MEDIUM,
  SLIDE_START_LARGE,
  SLIDE_START_XLARGE,
  SLIDE_END_SMALL,
  SLIDE_END_MEDIUM,
  SLIDE_END_LARGE,
  SLIDE_END_XLARGE,
  SCALE_DOWN_SMALL,
  SCALE_DOWN_MEDIUM,
  SCALE_DOWN_FULL,
  SCALE_UP_SMALL,
  SCALE_UP_MEDIUM,
  SCALE_UP_FULL,
  FADE_IN_SMALL,
  FADE_IN_FULL,
  FADE_OUT_SMALL,
  FADE_OUT_FULL,
  mergeAnimationStateObjects
};
